/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react'
import styles from './PlayInScreen.module.scss'
import checkVideoSync from '../../lib/checkVideoSync'
import Screen from '../Screen'
import Player, { PlayerInterface } from '@bt-react/components/Player'
import { PlayerState, PlayInScreenProps, PlayInScreenState } from './types'
import ScreenRowLayout from '../Screen/ScreenRowLayout'
import { PlayInObject, PlayInRemoteStatus } from '../../lib/playIn/types'
import { interval } from 'rxjs'
import { finalize, takeWhile } from 'rxjs/operators'
import throttle from 'lodash/throttle'
import get from 'lodash/get'
import { addEntry } from '../../lib/audit'

const _BDD = 'talks-play-in-player'
export const TOLERANCE = 0.3

function getDocState (playIn: PlayInScreenProps['playIn']): PlayerState {

  if (playIn.isPlaying()) return 'PLAYING'
  if (playIn.isPaused()) return 'PAUSED'
  return 'READY'

}

class PlayInScreen extends React.Component<PlayInScreenProps, PlayInScreenState> {

  private isSeeking = false
  private wasPlayingBeforeSeeking

  _isMounted = false
  playerRef: PlayerInterface | null = null
  playQueued = false

  refCallback = node => {

    if (!node) return
    this.playerRef = node

    this.setState({ refSet: true })

  }

  state: PlayInScreenState = {
    eventSubscriptionSet: false,
    hasError: false,
    initialTimeSet: false,
    docState: 'READY',
    refSet: false,
    playerReadyToPlay: false,
    playerRequestPending: false,
    playerState: 'READY',
    stateToApply: undefined,
    sourceUrl : this.props.sources?.[0]
  }

  static getDerivedStateFromError () {

    return { hasError: true }

  }

  componentDidMount () {

    addEntry('PresenterService', 'PlayInScreenSources', JSON.stringify({
      'propSources' : this.props.sources,
      'stateUrl': this.state.sourceUrl,
      'bridge': this.props.bridgeName
    }), 'info', this.props.BTID ?? '')
    this._isMounted = true

    if (this.shouldHandleRemoteStatusRequest(this.props.remoteStatusRequest)) {

      this.handleRemoteStatusRequest(this.props.remoteStatusRequest)

    }

    this.setMediaStatus()

    if (this.shouldApplyPlayState() && this.state.stateToApply !== 'PLAY') {

      this.setState({ stateToApply: 'PLAY' })

    }

    if (this.shouldApplyPausedStated() && this.state.stateToApply !== 'PAUSE') {

      this.setState({ stateToApply: 'PAUSE' })

    }

  }

  componentDidUpdate (prevProps, prevState) {

    const hasPropChanged = (...props: Partial<keyof PlayInScreenProps>[]): boolean =>
      props.some(prop => prevProps[prop] !== this.props[prop])
    const hasStateChanged = (...params: Partial<keyof PlayInScreenState>[]): boolean =>
      params.some(param => prevState[param] !== this.state[param])

        if (hasPropChanged('sources') || hasStateChanged('sourceUrl')) {
          addEntry('PresenterService', 'PlayInScreenSourcesChanged', JSON.stringify({
            'propSources' : this.props.sources,
            'stateUrl': this.state.sourceUrl,
            'bridge': this.props.bridgeName
          }), 'info', this.props.BTID ?? '')
        }

    if (hasStateChanged('refSet')) this.setCurrentTime(this.props.currentPos)

    // Only trigger if a request is pending
    if (this.props.remoteStatusRequest && hasPropChanged('remoteStatusRequest')) {

      // Should I trigger the update to the play-in doc
      if (this.shouldHandleRemoteStatusRequest(prevProps.remoteStatusRequest)) {

        this.handleRemoteStatusRequest(this.props.remoteStatusRequest)

      }

    }

    if (this.props.isMediaPresenter) {

      if (this.state.initialTimeSet && !this.state.eventSubscriptionSet) {

        this.subscribeToPoll()

      }

    }
    else {

      if (
        hasPropChanged('currentPos') ||
        prevState.refSet !== this.state.refSet
      ) this.setAdjustedTime(this.props.currentPos)

      if (this.shouldSetPausedTime()) {

        this.setAdjustedTime(this.props.pausedAt as number, true)

      }

    }

    if (hasPropChanged('sources')) this.setState({ playerReadyToPlay: false, sourceUrl : this.props.sources?.[0] })
    if (hasStateChanged('sourceUrl')) this.setState({ playerReadyToPlay: true })
    if (hasPropChanged('status')) this.setMediaStatus()

    if ( hasStateChanged('playerReadyToPlay', 'playerRequestPending', 'refSet',
      'playerState', 'docState', 'stateToApply', 'sourceUrl') ) {

      this.checkAndApplyStateChanges(prevProps, prevState)

    }

  }

  componentWillUnmount () {

    this._isMounted = false

  }

  checkAndApplyStateChanges = async (prevProps: PlayInScreenProps, prevState: PlayInScreenState) => {

    const hasPropChanged = (...props: Partial<keyof PlayInScreenProps>[]): boolean =>
      props.some(prop => prevProps[prop] !== this.props[prop])
    const hasStateChanged = (...params: Partial<keyof PlayInScreenState>[]): boolean =>
      params.some(param => prevState[param] !== this.state[param])

    // Apply the state if changed BEFORE checking for further state changes
    if (hasStateChanged('stateToApply')) {

      await this.applyStateToVideo()

    }

    if (hasPropChanged('sources') || hasStateChanged('playerReadyToPlay', 'playerRequestPending', 'refSet', 'playerState', 'docState')) {


      if (this.shouldApplyPlayState() && this.state.stateToApply !== 'PLAY') {

        this.setState({ stateToApply: 'PLAY' })

        return

      }
      else {

        if (this.shouldApplyPausedStated() && this.state.stateToApply !== 'PAUSE') {

          this.setState({ stateToApply: 'PAUSE' })

        }

      }

    }

  }

  shouldSetPausedTime = (): boolean => {

    const currentTime = this.playerRef?.currentTime
    return typeof currentTime === 'undefined' || typeof this.props.pausedAt === 'undefined' || this.props.pausedAt === null
      ? false
      : !this.approximatelyEqual(currentTime, this.props.pausedAt)

  }

  approximatelyEqual = (a: number, b: number, tolerance = TOLERANCE): boolean => Math.abs(a - b) <= tolerance

  // Check whether this participant should be the one to enact the play / pause remote request
  shouldHandleRemoteStatusRequest (prevRemoteStatusRequest: PlayInScreenProps['remoteStatusRequest']): boolean {

    if (!this.props.isMediaPresenter || this.props.remoteStatusRequest === null) return false
    return get(prevRemoteStatusRequest, 'status') !== get(this.props.remoteStatusRequest, 'status')

  }

  setMediaStatus = (): void => {

    if (!this._isMounted || !this.props.playIn) return

    const docState = getDocState(this.props.playIn)

    this.setState({ docState })

  }

  checkTimeIsFinite (time: number): boolean {

    if (!Number.isFinite(time)) {

      return false

    }

    return true

  }

  /** Set the current time in the video. Check if it's within a two second tolerance if it's not paused */
  setAdjustedTime = (time: number, paused = false): void => {

    if (!this.playerRef || this.isSeeking) return

    const currentTime = this.playerRef.currentTime
    const adjustedTime = paused ? time : checkVideoSync(currentTime, time)

    if (adjustedTime !== currentTime && this.checkTimeIsFinite(adjustedTime)) this.playerRef.currentTime = adjustedTime

  }

  /** Set the current time in the video without running any checks */
  setCurrentTime = (time: number): void => {

    if (!this.playerRef || !this.checkTimeIsFinite(time)) return

    try {

      this.playerRef.currentTime = time

    }
    catch (e) {

      return

    }

    if (!this.state.initialTimeSet) this.setState({ initialTimeSet: true })

  }

  shouldApplyPlayState = (): boolean => {

    if (!this.playerRef) return false

    return this.state.docState === 'PLAYING' &&
      !!this.props.sources?.length &&
      this.playerRef.paused &&
      !this.state.playerRequestPending &&
      this.state.playerReadyToPlay

  }

  shouldApplyPausedStated = (): boolean => {

    if (!this.playerRef) return false

    return this.state.docState === 'PAUSED' &&
      !this.state.playerRequestPending &&
      !this.playerRef.paused

  }

  // Controls play and pause on the Player / Video Element itself based on the state
  applyStateToVideo = async (): Promise<void> => {


    if (!this.playerRef || !this.state.stateToApply) return


    if (this.state.stateToApply === 'PLAY') {

      const { startAt = 0 } = this.props.playIn.doc

      this.setState({ playerRequestPending: true })


      try {

        await this.playerRef.play(startAt)

        this.playQueued = false

      }
      catch (e) {

        this.playQueued = true

      }
      finally {

        // Reset stateToApply here given it's async
        this.setState({ playerRequestPending: false, stateToApply: undefined })

      }

    }

    if (this.state.stateToApply === 'PAUSE') {

      const { pausedAt } = this.props.playIn.doc
      this.playerRef.pause(pausedAt === null ? undefined : pausedAt)
      this.setState({ stateToApply: undefined })

    }

  }

  // Sets the Remote Request on the Sync Doc
  onPlayerPlay = (): void => {

    if (
      !this._isMounted ||
      this.props.isBridgeInstance
    ) return

    this.props.playIn.setRemoteStatus(PlayInRemoteStatus.PLAY_REQUESTED)

  }

  // Sets the Remote Request on the Sync Doc
  onPlayerPause = (position?: number): void => {

    if (
      !this._isMounted ||
      this.props.isBridgeInstance
    ) return

    if (get(this.props.remoteStatusRequest, 'status') === PlayInRemoteStatus.PAUSE_REQUESTED) return

    this.props.playIn.setRemoteStatus(PlayInRemoteStatus.PAUSE_REQUESTED, position)

  }

  onSeek = () => {

    if (!this.isSeeking) {

      this.wasPlayingBeforeSeeking = this.state.docState === 'PLAYING'
      this.isSeeking = true

    }

  }

  onSeekComplete = (position: number): void => {

    if (!this._isMounted) return
    this.isSeeking = false

    const resume = this.isSeeking ? this.wasPlayingBeforeSeeking : this.state.docState === 'PLAYING'

    addEntry('PresenterService', 'PlayInSeekRequested', 'Play-In seeking', 'info', this.props.BTID ?? '')
    this.props.playIn.setRemoteStatus(PlayInRemoteStatus.SEEK_COMPLETED, position, resume)

  }

  handleStop = throttle((manuallyEnded = false): void => {

    if (this._isMounted && this.props.onStop) {

      if (manuallyEnded) addEntry('PresenterService', 'PlayInEndRequested', `Play-In end requested`, 'info', this.props.BTID ?? '')
      this.props.onStop(this.props.layoutsActive)

    }

  }, 500)

  handleReadyToPlay = (): void => {

    if (!this._isMounted) return

    this.setState({ playerReadyToPlay: true })

    if (this.props.pausedAt && this.playerRef) this.playerRef.currentTime = this.props.pausedAt

    if (this.playQueued) this.applyStateToVideo()

  }

  handlePause = () => this.setState({ playerState: 'PAUSED' })
  handlePlay = () => this.setState({ playerState: 'PLAYING' })

  subscribeToPoll (): void {

    if (this.state.eventSubscriptionSet) return

    interval(2000)
      .pipe(
        takeWhile(() => this._isMounted && this.props.isMediaPresenter),
        finalize(() => this.setState({ eventSubscriptionSet: false }))
      )
      .subscribe(this.writePlayerTimeToDocument)

    this.setState({ eventSubscriptionSet: true })
  }

  isMP4 = url => url.includes( '.mp4' )
  isFirstMP4 = url => url.split('/').pop().match( /-[0-9]-0.mp4$/g )
  failoverUrl = url => url.replace( /(-[0-9]-)0(.mp4)$/i, '$11$2' )

  handleError = (e) => {

    addEntry('PresenterService', 'PlayInPlayerError', JSON.stringify({
      'propSources' : this.props.sources,
      'stateUrl': this.state.sourceUrl,
      'bridge': this.props.bridgeName
    }), 'info', this.props.BTID ?? '')

    if( this.isMP4( this.state.sourceUrl ) && this.isFirstMP4(  this.state.sourceUrl ) ) {

      this.setState({
        sourceUrl: this.failoverUrl(  this.state.sourceUrl )
      })
    }
    else{

      this.setState({
        sourceUrl: this.props.sources[ 1 ]
      })
    }

    this.props.playIn.start(this.props.pausedAt )

  }

  writePlayerTimeToDocument = () => {

    if (!this.playerRef || this.isSeeking) return null

    if (
      this.playerRef &&
      this.state.docState === 'PLAYING' &&
      this.playerRef.currentTime !== this.props.currentPos &&
      !this.props.remoteStatusRequest
    ) {

      this.props.playIn.setCurrentPos(this.playerRef.currentTime)
    }

  }

  // Actions the update to Sync Doc to trigger the remote request - should only be the mediaPresenter
  handleRemoteStatusRequest = (remoteStatusRequest: PlayInObject['remoteStatusRequest']): void => {

    if (!this._isMounted || remoteStatusRequest === null || !this.playerRef) return

    switch (remoteStatusRequest.status) {

      case PlayInRemoteStatus.PLAY_REQUESTED:
        this.props.playIn.start(this.props.pausedAt || this.playerRef.currentTime)
        break

      case PlayInRemoteStatus.PAUSE_REQUESTED:
        this.props.playIn.pause(this.playerRef.currentTime)
        break

      case PlayInRemoteStatus.SEEK_COMPLETED:

        if (remoteStatusRequest.value) {

          this.setCurrentTime(remoteStatusRequest.value)

          if (remoteStatusRequest.resumePlaying) {

            this.props.playIn.start(remoteStatusRequest.value)

          }
          else {

            this.props.playIn.pause(remoteStatusRequest.value)

          }

        }

        break

      default:
        break

    }

  }

  onRemoteClick = (event, value?) => {

    switch (event) {

      case 'pause':
        addEntry('PresenterService', 'PlayInPauseRequested', `Pause requested (${value ?? ''})`, 'info', this.props.BTID ?? '')
        return this.onPlayerPause(value)
      case 'play':
        addEntry('PresenterService', 'PlayInStartRequested', 'Start requested', 'info', this.props.BTID ?? '')
        return this.onPlayerPlay()

    }

  }

  render () {

    if (!this.props.playIn) return null

    return <Screen>
      <ScreenRowLayout>
        <div className={styles['PlayIn-Container']} >
          <Player
            muteAudio={this.props.isMuted}
            controlsVisible={this.props.showControls ?? true}
            onReadyToPlay={this.handleReadyToPlay}
            source={this.state.sourceUrl}
            disableAutoplay={true}
            dataBdd={_BDD}
            ref={this.refCallback}
            onEnd={this.handleStop}
            onSeekComplete={this.onSeekComplete}
            onRemoteClick={this.onRemoteClick}
            onPause={this.handlePause}
            onPlay={this.handlePlay}
            onError={this.handleError}
          />
        </div>
      </ScreenRowLayout>
    </Screen>

  }

}

export default PlayInScreen
